import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { format, parseISO } from 'date-fns';
import { iSprintResDto } from 'src/app/helpers/Interfaces/sprint';

export interface iHistoryItem {
  id?: string;
  title: string;
  highlight: string;
  description: string;
  formattedDate: string;
  color?: string;
}

@Component({
  selector: 'app-history-popover',
  templateUrl: './history-popover.component.html',
  styleUrls: ['./history-popover.component.scss'],
})
export class HistoryPopoverComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string = 'Sprint History';
  @Input() tooltip: string = 'Sprint History';
  @Input() task: any = null;
  @Input() histories: any[] | null = null;
  @Input() sprints: iSprintResDto[] = [];
  @Input() iconClass: string = 'feather ficon icon-clock';
  @Input() popoverClass: string = 'view-details-history-popover';
  @Input() placement: string = 'auto';

  @ViewChild('popover') popoverRef!: NgbPopover;

  parsedHistories: iHistoryItem[] = [];
  hasHistories: boolean = false;
  private _isOpen = false;

  ngOnInit(): void {
    this._resolveHistories();
    window.addEventListener('scroll', this._onDocumentScroll, true);
    window.addEventListener('resize', this._onDocumentScroll, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['task'] || changes['histories'] || changes['sprints']) {
      this._resolveHistories();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this._onDocumentScroll, true);
    window.removeEventListener('resize', this._onDocumentScroll, true);
  }

  private _resolveHistories(): void {
    const rawList = this._getRawHistories();
    if (!rawList || rawList.length === 0) {
      this.parsedHistories = [];
      this.hasHistories = false;
      return;
    }

    const sorted = [...rawList].sort((a, b) => {
      const dateA = (a.createdDate || a.CreatedDate || a.changedAt)
        ? new Date(a.createdDate || a.CreatedDate || a.changedAt).getTime()
        : 0;
      const dateB = (b.createdDate || b.CreatedDate || b.changedAt)
        ? new Date(b.createdDate || b.CreatedDate || b.changedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    this.parsedHistories = sorted.map((h) => this._mapToHistoryItem(h));
    this.hasHistories = this.parsedHistories.length > 0;
  }

  private _getRawHistories(): any[] {
    if (this.histories && Array.isArray(this.histories) && this.histories.length > 0) {
      return this.histories;
    }

    if (!this.task) return [];

    const dynamicTask = this.task;
    const taskHistories =
      dynamicTask?.sprintHistories ||
      dynamicTask?.SprintHistories ||
      dynamicTask?.sprintHistory ||
      dynamicTask?.SprintHistory ||
      dynamicTask?.statusHistory ||
      [];

    if (Array.isArray(taskHistories) && taskHistories.length > 0) {
      return taskHistories;
    }

    const currentSprintId =
      dynamicTask?.sprintId || dynamicTask?.SprintId || dynamicTask?.sprint?.id;
    const currentSprintName =
      dynamicTask?.sprint ||
      dynamicTask?.Sprint ||
      dynamicTask?.sprint?.name ||
      dynamicTask?.sprintTitle;
    const createdDate =
      dynamicTask?.createdDate || dynamicTask?.CreatedDate || dynamicTask?.startDate;

    if (currentSprintId || currentSprintName) {
      return [
        {
          id: dynamicTask.id,
          taskId: dynamicTask.id,
          sprintId: currentSprintId,
          sprintName:
            typeof currentSprintName === 'string'
              ? currentSprintName
              : (currentSprintName?.name || 'Current Sprint'),
          createdDate: createdDate || new Date().toISOString(),
        },
      ];
    }

    return [];
  }

  private _mapToHistoryItem(h: any): iHistoryItem {
    if (h.changedBy || h.status) {
      return {
        id: h.id,
        title: h.changedBy || 'User',
        highlight: h.status || '',
        description: 'Changed status to',
        formattedDate: this._formatDate(h.changedAt || h.createdDate),
        color: h.color || '#7367f0',
      };
    }

    const sprintName = this._resolveSprintName(h);
    const dateStr = h.createdDate || h.CreatedDate || h.date;

    return {
      id: h.id,
      title: sprintName,
      highlight: sprintName,
      description: '',
      formattedDate: this._formatDate(dateStr),
      color: h.color || '#7367f0',
    };
  }

  private _resolveSprintName(history: any): string {
    if (!history) return 'Sprint';

    const sprintName =
      history.sprintName ||
      history.SprintName ||
      history.sprintTitle ||
      history.SprintTitle ||
      history.sprint?.name ||
      history.Sprint?.name ||
      history.sprint?.title ||
      history.Sprint?.title ||
      (typeof history.sprint === 'string' ? history.sprint : null) ||
      (typeof history.Sprint === 'string' ? history.Sprint : null);

    if (sprintName && String(sprintName).trim()) {
      return String(sprintName).trim();
    }

    const sprintId = history.sprintId || history.SprintId;
    if (sprintId && this.sprints?.length) {
      const found = this.sprints.find((s) => s.id === sprintId);
      if (found?.name) return found.name;
    }

    const dynamicTask = this.task;
    const taskSprintId = dynamicTask?.sprintId || dynamicTask?.SprintId;
    const taskSprint = dynamicTask?.sprint || dynamicTask?.Sprint;

    if (sprintId && taskSprintId && sprintId === taskSprintId && taskSprint) {
      return typeof taskSprint === 'string'
        ? taskSprint
        : (taskSprint.name || 'Sprint');
    }

    if (sprintId) {
      return `Sprint (${String(sprintId).substring(0, 8)})`;
    }

    return 'Sprint';
  }

  private _formatDate(dateStr?: string): string {
    if (!dateStr) return '—';
    try {
      const cleanDate = String(dateStr).replace(/[zZ]/g, '');
      const parsed = parseISO(cleanDate);
      if (isNaN(parsed.getTime())) {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? String(dateStr) : format(d, 'MMM do, yyyy p');
      }
      return format(parsed, 'MMM do, yyyy p');
    } catch {
      return String(dateStr);
    }
  }

  togglePopover(event: MouseEvent, popover: NgbPopover): void {
    event.preventDefault();
    event.stopPropagation();

    if (popover.isOpen()) {
      popover.close();
      this._isOpen = false;
    } else {
      popover.open();
      this._isOpen = true;
    }
  }

  closePopover(): void {
    if (this.popoverRef && this.popoverRef.isOpen()) {
      this.popoverRef.close();
      this._isOpen = false;
    }
  }

  onPopoverHidden(): void {
    this._isOpen = false;
  }

  private _onDocumentScroll = (event: Event): void => {
    if (!this._isOpen || !this.popoverRef || !this.popoverRef.isOpen()) return;

    const target = event.target as HTMLElement;
    if (
      target &&
      (target.classList?.contains('popover-body') ||
        target.closest?.('.view-details-history-popover'))
    ) {
      return;
    }

    this.closePopover();
  };
}
